import React from 'react';
import { djangoUrls } from '../../config/urls';
import NavLink from '../NavLink/NavLink';
import { useClient } from '../../contexts/ClientContext';
import { resolveClientLogoDisplayUrl } from '../../utils/clientBranding';
import { useFeatures } from '../../contexts/FeaturesContext';
import './Footer.css';

const DEFAULT_LOGO_URL = '/static/logo.png';
const SEAHIRE_URL = 'https://seahire.co.in';

const Footer = () => {
  // Get client metadata (footer_tab from meta API, stored in localStorage under client key)
  const { companyName, logo: clientLogo, tagline, contact, social, apps, footer_tab: footerTabRaw } = useClient();
  const { hasFeature } = useFeatures();
  const hidePoweredBy = hasFeature('hide_powered_by');
  const logoUrl = resolveClientLogoDisplayUrl(clientLogo) || DEFAULT_LOGO_URL;

  // Normalize footer_tab: New format { tab_name, sub_tabs } or legacy array
  const getFooterTabData = (footerTab) => {
    if (!footerTab) return { tab_name: 'Quick Links', sub_tabs: [] };
    if (Array.isArray(footerTab)) {
      // Legacy format: convert array to new structure
      return {
        tab_name: 'Quick Links',
        sub_tabs: footerTab.map(item => ({
          label: typeof item === 'string' ? item : (item.label || ''),
          url: typeof item === 'string' ? '#' : (item.url || item.slug ? `/p/${item.slug}/` : '#')
        })).filter(item => item.label)
      };
    }
    // New format: { tab_name, sub_tabs }
    return footerTab;
  };

  const footerTabData = getFooterTabData(footerTabRaw);
  const footerTabItems = footerTabData.sub_tabs || [];
  // Column 2 heading: use footer tab name if available, else "Quick Links"
  const column2Heading = (footerTabRaw && footerTabData.tab_name) ? footerTabData.tab_name : 'Quick Links';

  // Column 2 links: About always; then Jobs/Employers or footer_tab sub_tabs
  // 0 sub_tabs → About, Jobs, Employers | 1 sub_tab → About, [sub_tab], Employers | 2+ → About, [all sub_tabs]
  const renderLink = (item) => {
    const isExternal = item.url?.startsWith('http://') || item.url?.startsWith('https://');
    return isExternal ? (
      <a href={item.url} target="_blank" rel="noopener noreferrer">{item.label}</a>
    ) : (
      <NavLink to={item.url || '#'}>{item.label}</NavLink>
    );
  };

  const quickLinksItems = (() => {
    const about = { label: 'About', url: djangoUrls.about_us };
    if (footerTabItems.length === 0) {
      return [about, { label: 'Jobs', url: djangoUrls.all_jobs }, { label: 'Employers', url: djangoUrls.recruitment_solutions }];
    }
    if (footerTabItems.length === 1) {
      return [about, footerTabItems[0], { label: 'Employers', url: djangoUrls.recruitment_solutions }];
    }
    return [about, ...footerTabItems];
  })();

  return (
    <footer className="footer">
      <div className="container footer-content">
        <div className="footer-grid">
          {/* Column 1: Logo, tagline, social, app download */}
          <div className="footer-column footer-column-brand">
            <div className="footer-brand-row">
              <div className="footer-logo-container">
                <img src={logoUrl} alt={companyName || 'Seahire Marine Hiring Platform'} className="footer-logo" style={{ height: '60px' }} />
              </div>
              <p className="footer-tagline">{tagline || 'Connecting marine professionals with opportunities worldwide.'}</p>
            </div>
            {(social && (social.facebook || social.twitter || social.linkedin || social.instagram)) && (
              <div className="footer-social">
                {social.facebook && (
                  <a href={social.facebook} title="Facebook" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                )}
                {social.twitter && (
                  <a href={social.twitter} title="Twitter" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-twitter"></i>
                  </a>
                )}
                {social.linkedin && (
                  <a href={social.linkedin} title="LinkedIn" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                )}
                {social.instagram && (
                  <a href={social.instagram} title="Instagram" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-instagram"></i>
                  </a>
                )}
              </div>
            )}
            {(apps && (apps.android || apps.ios)) && (
              <div className="app-download-buttons">
                {apps.android && (
                  <a href={apps.android} className="app-download-btn" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-google-play"></i>
                    <div className="app-download-text">
                      <span>GET IT ON</span>
                      <span>Google Play</span>
                    </div>
                  </a>
                )}
                {apps.ios && (
                  <a href={apps.ios} className="app-download-btn" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-apple"></i>
                    <div className="app-download-text">
                      <span>Download</span>
                      <span>App Store</span>
                    </div>
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Column 2: Quick Links / footer_tab - About always; Jobs/Employers or footer_tab sub_tabs */}
          <div className="footer-column">
            <h5>{column2Heading}</h5>
            <div className={quickLinksItems.length > 5 ? 'footer-links-scroll' : ''}>
              <ul className="footer-links">
                {quickLinksItems.map((item, idx) => (
                  <li key={idx}>{renderLink(item)}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 3: Candidates */}
          <div className="footer-column">
            <h5>Candidates</h5>
            <ul className="footer-links">
              <li><NavLink to={djangoUrls.community}>Community</NavLink></li>
              <li><NavLink to={djangoUrls.settings}>Job Alerts</NavLink></li>
              <li><NavLink to={djangoUrls.application_timeline}>Applications</NavLink></li>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div className="footer-column">
            <h5>Contact</h5>
            <ul className="footer-links">
              <li><a href={djangoUrls.contact || "#"}><i className="fas fa-map-marker-alt"></i> Location</a></li>
              <li><a href={`tel:${contact?.phone?.replace(/[^0-9+]/g, '') || '+5551234567'}`}><i className="fas fa-phone"></i>{contact?.phone || '(555) 123-4567'}</a></li>
              <li><a href={`mailto:${contact?.email || 'info@seahire.com'}`}><i className="fas fa-envelope"></i>Email Us</a></li>
            </ul>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-copyright">
            &copy; 2026 Seahire Marine Services Private Limited. All rights reserved.
          </div>
          {!hidePoweredBy && (
            <>
              <span>•</span>
              <div className="footer-powered-by">
                <a href={SEAHIRE_URL} target="_blank" rel="noopener noreferrer">Powered by Seahire</a>
              </div>
            </>
          )}
          <span>•</span>
          <div className="footer-legal">
            <a href={djangoUrls.privacy_policy || "#"}>Privacy</a>
            <span>•</span>
            <a href={djangoUrls.terms_of_service || "#"}>Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

